// ComunicacionPiezas — /admin/comunicacion/piezas (26-jul-2026)
// Pasos 3, 4 y 5 del flujo: el Gerente escribe → VOS aprobás cada pieza (acá SÍ
// ves texto e imagen, con el mockup de celular) → el Publisher la sube solo.
// Reemplaza la mitad de abajo de ComunicacionRedes con estados vacíos que
// explican POR QUÉ está vacío (el caso del 26-jul: plan aprobado, 2 de 6
// escritas, todo se veía en 0 y Nico no entendía por qué).

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { COM_CSS } from '../../components/comunicacion/ComunicacionUI'
import { FlujoStepper, CasoActualCard, BotonAgente, cargarEstadoFlujo, FLUJO_CSS } from '../../components/comunicacion/FlujoRedes'
import { PiezaModalIG, imagenesDePieza, PIEZA_PREVIEW_CSS } from '../../components/comunicacion/PiezaPreviewIG'

// ⚠️ posts_ig NO tiene columna `modelo` — no agregarla acá (tira 400).
const COLS_PIEZA = 'id, brief, pilar, formato, caption, caption_facebook, hashtags, slides, cantidad_slides, imagenes_urls, imagenes_renders, horario_sugerido, estado, score_gerente, feedback_gerente, trace_dialogo, brief_disenio, engine_render, agente_owner, redes, created_at'

export default function ComunicacionPiezas() {
  const [loading, setLoading] = useState(true)
  const [est, setEst] = useState(null)
  const [cola, setCola] = useState([])
  const [programadas, setProgramadas] = useState([])
  const [msg, setMsg] = useState(null)
  const [piezaAbierta, setPiezaAbierta] = useState(null)
  // Edición inline de la pieza antes de aprobar (texto + hashtags).
  const [editId, setEditId] = useState(null)
  const [editCap, setEditCap] = useState('')
  const [editTags, setEditTags] = useState('')
  const [guardando, setGuardando] = useState(false)

  useEffect(() => { cargar() }, [])

  useEffect(() => {
    if (piezaAbierta != null && !loading && cola.length === 0) setPiezaAbierta(null)
  }, [cola, loading, piezaAbierta])

  async function cargar() {
    setLoading(true)
    try {
      const estado = await cargarEstadoFlujo()
      setEst(estado)

      // Piezas terminadas que esperan tu OK (con TODO el detalle para el modal celular)
      const { data: posts } = await supabase
        .from('posts_ig')
        .select(COLS_PIEZA)
        .eq('estado', 'generado')
        .order('horario_sugerido', { ascending: true })
        .limit(12)
      setCola(posts || [])

      // Ya aprobadas / programadas / publicadas / fallidas
      const { data: prog } = await supabase
        .from('posts_ig')
        .select(COLS_PIEZA + ', approved_at, publicado_at, ig_media_id, publicacion_log')
        .in('estado', ['aprobado', 'programado', 'publicado', 'fallido'])
        .order('horario_sugerido', { ascending: false, nullsFirst: false })
        .limit(12)
      setProgramadas(prog || [])
    } catch (e) {
      console.error('ComunicacionPiezas cargar:', e)
    } finally {
      setLoading(false)
    }
  }

  async function aprobarPieza(id) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { alert('Sesión expirada'); return }
    const { error } = await supabase.from('posts_ig')
      .update({ estado: 'aprobado', approved_at: new Date().toISOString(), approved_by: user.id })
      .eq('id', id)
    if (error) { alert('Error: ' + error.message); return }
    setMsg({ tipo: 'ok', texto: '✓ Pieza aprobada = programada. El Publisher la sube solo a Instagram cuando llegue su horario (abajo la ves en la cola de salida).' })
    await cargar()
  }

  // El rechazo con motivo alimenta el loop de aprendizaje (Gerente + Estratega lo leen)
  async function guardarRechazo(id, motivo) {
    const { data: actual } = await supabase.from('posts_ig').select('feedback_gerente').eq('id', id).single()
    const prev = actual?.feedback_gerente || []
    const { error } = await supabase.from('posts_ig')
      .update({ estado: 'rechazado', feedback_gerente: [...prev, motivo || '(sin motivo)'] })
      .eq('id', id)
    if (error) { alert('Error: ' + error.message); return }
    await cargar()
  }

  async function rechazarPieza(id) {
    const motivo = prompt('¿Por qué la rechazás? (queda guardado: el Gerente y el Estratega aprenden de esto)')
    if (motivo === null) return
    await guardarRechazo(id, motivo)
  }

  // Abre el editor inline con el texto/hashtags actuales de la pieza.
  function abrirEdicion(p) {
    setEditId(p.id)
    setEditCap(p.caption || '')
    setEditTags((Array.isArray(p.hashtags) ? p.hashtags : []).join(' '))
  }

  // Guarda la edición a mano. caption_editado_a_mano=true marca que lo tocó Nico
  // (para no pisarlo si el Gerente reprocesa).
  async function guardarEdicion(id) {
    setGuardando(true)
    const tags = editTags.split(/\s+/).map(t => t.trim()).filter(Boolean)
      .map(t => (t.startsWith('#') ? t : '#' + t))
    const { error } = await supabase.from('posts_ig')
      .update({ caption: editCap, hashtags: tags, caption_editado_a_mano: true })
      .eq('id', id)
    setGuardando(false)
    if (error) { alert('Error al guardar: ' + error.message); return }
    setEditId(null)
    setMsg({ tipo: 'ok', texto: '✓ Cambios guardados. Lo que ves es lo que sale.' })
    await cargar()
  }

  const c = est?.c
  const esperandoGerente = c?.esperanGerente || 0
  const planPropuesto = est?.plan?.estado === 'propuesto'
  const sinPlan = !est?.plan

  return (
    <AdminLayout title="2 · Publicar">
      <div className="com-wrap">

        {/* QUÉ ES ESTA PANTALLA */}
        <div className="com-hero">
          <div className="com-hero-avatar">📱</div>
          <div className="com-hero-body">
            <div className="com-hero-eyebrow">COMUNICACIÓN · PASOS 3, 4 Y 5 DE 5</div>
            <h1 className="com-hero-nombre">Piezas</h1>
            <div className="com-hero-rol">Acá SÍ ves texto e imagen — cada pieza como queda en Instagram</div>
            <p className="com-hero-desc">
              El Gerente escribe las piezas del plan aprobado (texto, hashtags, diseño). Vos las revisás una por una
              con el mockup de celular y las aprobás. Aprobar = programar: el Publisher la sube solo cuando llega su horario.
            </p>
          </div>
          <Link to="/admin/comunicacion" className="com-hero-back">← Panel</Link>
        </div>

        <FlujoStepper pasoActual={est?.paso || 3} />

        {msg && <div className={`com-msg ${msg.tipo === 'ok' ? 'com-msg-ok' : 'com-msg-err'}`}>{msg.texto}</div>}

        {est && <CasoActualCard est={est} onMsg={setMsg} onDone={cargar} paginaActual="piezas" />}

        {/* COLA: ESPERAN TU OK */}
        <section className="com-card" style={{ borderLeft: '4px solid #C5A059' }}>
          <div className="com-card-head">
            <h2 className="com-card-title">✋ Esperan tu OK ({cola.length})</h2>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="com-card-sub">Tocá una pieza y la ves como queda en Instagram 📱</span>
              {esperandoGerente > 0 && <BotonAgente agente="gerente" onMsg={setMsg} onDone={cargar} className="com-btn com-btn-ghost" />}
            </div>
          </div>

          {/* ESTADOS VACÍOS QUE EXPLICAN (el fix del caso 26-jul) */}
          {!loading && cola.length === 0 && (
            <div className="com-empty">
              {esperandoGerente > 0 ? (
                <>
                  <strong>Todavía no hay piezas escritas — pero NO es un error.</strong>
                  Aprobaste el plan ✓ y quedan {esperandoGerente} tema{esperandoGerente !== 1 ? 's' : ''} esperando
                  al Gerente. Él corre solo todos los días y escribe de a 2 por corrida. Si no querés esperar,
                  apretá "Escribir ahora" arriba a la derecha y en 1-3 minutos aparecen acá.
                </>
              ) : planPropuesto ? (
                <>
                  <strong>Primero falta aprobar el plan (paso 2).</strong>
                  Hay un plan propuesto esperándote en <Link to="/admin/comunicacion/plan">1 · Plan semanal</Link>.
                  Hasta que no lo apruebes, el Gerente no escribe nada.
                </>
              ) : sinPlan ? (
                <>
                  <strong>No hay plan esta semana (paso 1).</strong>
                  El Estratega propone el plan los domingos 20:00, o pedíselo ahora
                  desde <Link to="/admin/comunicacion/plan">1 · Plan semanal</Link>.
                </>
              ) : (
                <>
                  <strong>Todo revisado ✓</strong>
                  No hay piezas esperando tu OK. Las que aprobaste están abajo, en la cola de salida.
                </>
              )}
            </div>
          )}

          <div className="cpz-cola">
            {cola.map((p, i) => {
              const miniatura = imagenesDePieza(p).find(Boolean) || null
              return (
                <div
                  key={p.id}
                  className="cpz-pieza cpz-pieza-click"
                  onClick={() => setPiezaAbierta(i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPiezaAbierta(i) } }}
                  title="Tocá para verla como queda en Instagram"
                >
                  <div className="cpz-pieza-head">
                    <span className="com-pill com-pill-teal">{p.formato || 'feed'}</span>
                    <span className="com-pill com-pill-gray">{p.pilar || '—'}</span>
                    {p.horario_sugerido && (
                      <span className="cpz-pieza-hora">
                        {new Date(p.horario_sugerido).toLocaleString('es-UY', { weekday: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className="cpz-pieza-cuerpo">
                    {miniatura ? (
                      <img src={miniatura} alt="" className="cpz-pieza-thumb" loading="lazy" />
                    ) : (
                      <div className="cpz-pieza-thumb cpz-pieza-thumb-vacia" title="La imagen final se compone al publicar">🖼️</div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div className="cpz-pieza-titulo">{(p.brief || '').split('\n')[0].slice(0, 80) || 'Sin título'}</div>
                      {p.caption && <div className="cpz-pieza-cap">{p.caption.slice(0, 150)}{p.caption.length > 150 ? '…' : ''}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={e => { e.stopPropagation(); setPiezaAbierta(i) }} className="com-btn com-btn-primary">📱 Ver como queda</button>
                    <button onClick={e => { e.stopPropagation(); editId === p.id ? setEditId(null) : abrirEdicion(p) }} className="com-btn com-btn-ghost" title="Editar el texto y los hashtags antes de aprobar">✏️ {editId === p.id ? 'Cerrar' : 'Editar'}</button>
                    <button onClick={e => { e.stopPropagation(); aprobarPieza(p.id) }} className="com-btn com-btn-green" title="Aprobar = programar: sale sola a su horario">✓ Aprobar y programar</button>
                    <button onClick={e => { e.stopPropagation(); rechazarPieza(p.id) }} className="com-btn com-btn-danger">✕ Rechazar</button>
                  </div>

                  {editId === p.id && (
                    <div className="cpz-edit" onClick={e => e.stopPropagation()}>
                      <label className="cpz-edit-lbl">Texto del posteo</label>
                      <textarea className="cpz-edit-cap" value={editCap} onChange={e => setEditCap(e.target.value)} rows={7} />
                      <label className="cpz-edit-lbl">Hashtags (separados por espacio)</label>
                      <input className="cpz-edit-tags" value={editTags} onChange={e => setEditTags(e.target.value)} placeholder="#Limpack #Montevideo …" />
                      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                        <button onClick={() => guardarEdicion(p.id)} disabled={guardando} className="com-btn com-btn-green">{guardando ? 'Guardando…' : '💾 Guardar cambios'}</button>
                        <button onClick={() => setEditId(null)} className="com-btn com-btn-ghost">Cancelar</button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        {/* COLA DE SALIDA + PUBLICADAS */}
        <section className="com-card">
          <div className="com-card-head">
            <h2 className="com-card-title">
              🚀 En cola de salida y publicadas
              {(() => {
                const esperando = programadas.filter(p => p.estado === 'aprobado' || p.estado === 'programado').length
                const yaSalieron = programadas.filter(p => p.estado === 'publicado').length
                return <span style={{ fontWeight: 400, opacity: .75, fontSize: '.85em' }}>
                  {' '}· {esperando} esperando horario · {yaSalieron} publicadas
                </span>
              })()}
            </h2>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="com-card-sub">El Publisher chequea cada 15 min y publica solo.</span>
              {(c?.vencidas || 0) > 0 && <BotonAgente agente="publisher" onMsg={setMsg} onDone={cargar} className="com-btn com-btn-ghost" />}
            </div>
          </div>

          {!loading && programadas.length === 0 && (
            <div className="com-empty">
              <strong>Todavía no aprobaste ninguna pieza.</strong>
              Cuando apruebes una arriba, aparece acá con su horario de salida — y de ahí sale sola a Instagram.
            </div>
          )}

          <div className="cpz-cola">
            {programadas.map(p => {
              const mini = imagenesDePieza(p).find(Boolean) || null
              const vencido = p.horario_sugerido && new Date(p.horario_sugerido) <= new Date()
              const esperando = p.estado === 'aprobado' || p.estado === 'programado'
              const pill = p.estado === 'publicado'
                ? { txt: '✅ Publicada en IG', cls: 'com-pill-green' }
                : p.estado === 'fallido'
                  ? { txt: '⚠ Falló al publicar', cls: 'com-pill-red' }
                  : vencido
                    ? { txt: '⏳ Sale en la próxima pasada del Publisher', cls: 'com-pill-gold' }
                    : { txt: '🕒 Programada — sale sola a su horario', cls: 'com-pill-gold' }
              return (
                <div key={p.id} className="cpz-pieza">
                  <div className="cpz-pieza-head">
                    <span className={`com-pill ${pill.cls}`}>{pill.txt}</span>
                    <span className="com-pill com-pill-gray">{p.formato || 'feed'}</span>
                    {p.horario_sugerido && (
                      <span className="cpz-pieza-hora">
                        {new Date(p.horario_sugerido).toLocaleString('es-UY', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className="cpz-pieza-cuerpo">
                    {mini ? (
                      <img src={mini} alt="" className="cpz-pieza-thumb" loading="lazy" />
                    ) : (
                      <div className="cpz-pieza-thumb cpz-pieza-thumb-vacia" title={esperando ? 'La imagen final se compone al publicar' : 'Sin imagen guardada'}>🖼️</div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div className="cpz-pieza-titulo">{(p.brief || '').split('\n')[0].slice(0, 80) || 'Sin título'}</div>
                      {p.caption && <div className="cpz-pieza-cap">{p.caption.slice(0, 130)}{p.caption.length > 130 ? '…' : ''}</div>}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="com-card-sub" style={{ marginTop: 12 }}>
            ¿Cómo rindió lo publicado? → <Link to="/admin/marketing/audiencias">Resultados</Link> ·
            ¿Editar una pieza a fondo (texto, imagen, slides)? → <Link to="/admin/marketing">Taller avanzado</Link>
          </div>
        </section>

      </div>

      {/* Modal celular: la pieza exactamente como se ve en Instagram */}
      {(() => {
        if (piezaAbierta == null || cola.length === 0) return null
        const idx = Math.min(piezaAbierta, cola.length - 1)
        const pieza = cola[idx]
        if (!pieza) return null
        return (
          <PiezaModalIG
            pieza={pieza}
            indice={idx}
            total={cola.length}
            onCerrar={() => setPiezaAbierta(null)}
            onAnterior={idx > 0 ? () => setPiezaAbierta(idx - 1) : null}
            onSiguiente={idx < cola.length - 1 ? () => setPiezaAbierta(idx + 1) : null}
            onAprobar={() => aprobarPieza(pieza.id)}
            onRechazar={(motivo) => guardarRechazo(pieza.id, motivo)}
          />
        )
      })()}

      <style>{COM_CSS}</style>
      <style>{FLUJO_CSS}</style>
      <style>{PIEZA_PREVIEW_CSS}</style>
      <style>{`
        .cpz-cola { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
        @media (max-width: 800px) { .cpz-cola { grid-template-columns: 1fr; } }
        .cpz-pieza { border: 1px solid #EDF0F4; border-radius: 10px; padding: 12px; }
        .cpz-pieza-click { cursor: pointer; transition: border-color .12s, box-shadow .12s; }
        .cpz-pieza-click:hover, .cpz-pieza-click:focus-visible {
          border-color: #00A8B5; box-shadow: 0 2px 10px rgba(0,168,181,.12); outline: none;
        }
        .cpz-pieza-head { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
        .cpz-pieza-hora { font-size: 11px; color: #6B7280; margin-left: auto; }
        .cpz-pieza-cuerpo { display: flex; gap: 10px; margin-top: 8px; align-items: flex-start; }
        .cpz-pieza-thumb {
          width: 52px; height: 65px; border-radius: 8px; object-fit: cover; flex-shrink: 0;
          border: 1px solid #EDF0F4; background: #F1F5F9;
        }
        .cpz-pieza-thumb-vacia {
          display: flex; align-items: center; justify-content: center; font-size: 20px;
          background: linear-gradient(135deg, #1A3461, #0c1220); border: 0;
        }
        .cpz-pieza-titulo { font-size: 13px; font-weight: 800; color: #1A3461; }
        .cpz-pieza-cap { font-size: 11.5px; color: #6B7280; margin-top: 4px; line-height: 1.45; }
        .com-empty a { color: #00A8B5; font-weight: 700; }
        .cpz-edit { margin-top: 10px; padding: 12px; border: 1px solid #CFE8EC; border-radius: 10px; background: #F7FBFC; }
        .cpz-edit-lbl { display: block; font-size: 10.5px; font-weight: 800; color: #1A3461; text-transform: uppercase; letter-spacing: .04em; margin: 0 0 5px; }
        .cpz-edit-lbl + input { margin-bottom: 0; }
        .cpz-edit-cap, .cpz-edit-tags {
          width: 100%; border: 1.5px solid #DCE3EC; border-radius: 8px; padding: 9px 11px;
          font: inherit; font-size: 12.5px; color: #0f172a; line-height: 1.5; resize: vertical;
        }
        .cpz-edit-cap { min-height: 120px; margin-bottom: 10px; }
        .cpz-edit-cap:focus, .cpz-edit-tags:focus { outline: none; border-color: #00A8B5; box-shadow: 0 0 0 3px rgba(0,168,181,.12); }
      `}</style>
    </AdminLayout>
  )
}
